import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import Footer from '../components/Footer';
import { loadState, updatePage } from '../utils/stateManager';
import './Page7Tags.css';

function Page7Tags() {
  const navigate = useNavigate();
  const [defaultTags, setDefaultTags] = useState({});
  const [customTags, setCustomTags] = useState([]);

  useEffect(() => {
    const state = loadState();

    // Purpose code to long name mapping
    const purposeMap = {
      'mfg': 'Manufacturing',
      'ops': 'Operations',
      'eng': 'Engineering',
      'app': 'Application',
      'fin': 'Finance',
      'dba': 'Database',
      'web': 'WebServices',
      'api': 'APIServices',
      'rpt': 'Reporting',
      'dev': 'Development',
      'tst': 'Testing',
      'sec': 'Security',
      'net': 'Networking',
      'mon': 'Monitoring'
    };

    const purposeCode = state.page3VMConfig?.purpose || 'app';
    const purposeLongName = purposeMap[purposeCode] || 'Application';

    // Auto-populate default tags
    const defaults = {
      Environment: state.page3VMConfig?.environment || 'production',
      Region: state.page3VMConfig?.region || 'eastus',
      DeployedBy: 'Bicep',
      Purpose: purposeLongName,
      CreatedDate: new Date().toISOString().split('T')[0]
    };
    setDefaultTags(defaults);

    // Load custom tags if they exist
    if (state.page7Tags && state.page7Tags.customTags) {
      setCustomTags(state.page7Tags.customTags);
    }
  }, []);

  useEffect(() => {
    updatePage('page7Tags', { defaultTags, customTags });
  }, [customTags]);

  const handleAddTag = () => {
    setCustomTags([...customTags, { key: '', value: '' }]);
  };

  const handleRemoveTag = (index) => {
    const updated = customTags.filter((_, i) => i !== index);
    setCustomTags(updated);
  };

  const handleTagChange = (index, field, value) => {
    const updated = [...customTags];
    updated[index][field] = value;
    setCustomTags(updated);
  };

  const handleBack = () => {
    navigate('/page6');
  };

  const handleContinue = () => {
    navigate('/page8');
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Azure VM Deployment Wizard</h1>
        <ProgressBar currentPage={7} totalPages={9} />
      </div>

      <div className="page-content">
        <h2>Step 7: Resource Tags</h2>

        <div className="info-section">
          <h3>🏷️ Why Tags?</h3>
          <p>Tags help with cost tracking, automation, and organization. They're key-value pairs applied to Azure resources.</p>
        </div>

        <div className="default-tags-section">
          <h3>Default Tags (Auto-Populated)</h3>
          <p className="section-note">These tags are automatically generated from your configuration:</p>
          <div className="tags-grid">
            {Object.entries(defaultTags).map(([key, value]) => (
              <div key={key} className="tag-item default">
                <div className="tag-key">{key}</div>
                <div className="tag-value">{value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="custom-tags-section">
          <h3>Custom Tags</h3>
          <p className="section-note">Add additional tags specific to your organization:</p>

          <div className="examples-box">
            <strong>Example Tags:</strong>
            <ul>
              <li><code>CostCenter</code> = IT-Operations</li>
              <li><code>Owner</code> = john.doe@company.com</li>
              <li><code>Project</code> = CustomerPortalMigration</li>
              <li><code>Department</code> = Engineering</li>
              <li><code>Backup</code> = Daily</li>
            </ul>
          </div>

          <div className="custom-tags-list">
            {customTags.map((tag, index) => (
              <div key={index} className="custom-tag-row">
                <input
                  type="text"
                  className="tag-input key"
                  placeholder="Key (e.g., CostCenter)"
                  value={tag.key}
                  onChange={(e) => handleTagChange(index, 'key', e.target.value)}
                />
                <span className="separator">:</span>
                <input
                  type="text"
                  className="tag-input value"
                  placeholder="Value (e.g., IT-Operations)"
                  value={tag.value}
                  onChange={(e) => handleTagChange(index, 'value', e.target.value)}
                />
                <button
                  className="btn-remove"
                  onClick={() => handleRemoveTag(index)}
                  title="Remove tag"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button className="btn-add-tag" onClick={handleAddTag}>
            + Add Tag
          </button>
        </div>

        <div className="summary-section">
          <h3>📋 All Tags Summary</h3>
          <div className="tags-preview">
            {Object.entries(defaultTags).map(([key, value]) => (
              <div key={key} className="tag-preview-item">
                <strong>{key}:</strong> {value}
              </div>
            ))}
            {customTags.filter(t => t.key && t.value).map((tag, index) => (
              <div key={`custom-${index}`} className="tag-preview-item">
                <strong>{tag.key}:</strong> {tag.value}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="page-actions">
        <button className="btn-secondary" onClick={handleBack}>
          ← Back
        </button>
        <button className="btn-primary" onClick={handleContinue}>
          Preview Deployment →
        </button>
      </div>

      <Footer />
    </div>
  );
}

export default Page7Tags;
